import { useState } from 'react';
import type { RsvpRow, RsvpStatus, AdminPatchInput } from '../../lib/types';
import { RSVP_STATUSES } from '../../lib/types';
import { patchAdminRsvp, releaseCharacter, deleteRsvp } from '../../lib/api';

interface Props {
  rsvps: RsvpRow[];
  onChange: () => void;
}

const STATUS_LABEL: Record<RsvpStatus, string> = {
  coming: 'Coming',
  maybe: 'Maybe',
  not_coming: 'Not coming',
};

const COLUMN_LABELS = [
  'Icon',
  'Child',
  'Parent',
  'Status',
  'Character',
  'Shirt size',
  'Admin notes',
  'Actions',
] as const;

export function RsvpTable({ rsvps, onChange }: Props) {
  const [savingId, setSavingId] = useState<number | null>(null);

  async function patch(id: number, body: Partial<AdminPatchInput>) {
    setSavingId(id);
    try {
      await patchAdminRsvp(id, body);
      onChange();
    } finally {
      setSavingId((cur) => (cur === id ? null : cur));
    }
  }

  async function setStatus(id: number, status: RsvpStatus) {
    await patch(id, { rsvp_status: status });
  }
  async function setNotes(id: number, admin_notes: string) {
    await patch(id, { admin_notes });
  }
  async function release(id: number) {
    if (!confirm('Release this character?')) return;
    setSavingId(id);
    try {
      await releaseCharacter(id);
      onChange();
    } finally {
      setSavingId((cur) => (cur === id ? null : cur));
    }
  }
  async function remove(id: number) {
    if (!confirm('Delete this RSVP permanently? This cannot be undone.')) return;
    setSavingId(id);
    try {
      await deleteRsvp(id);
      onChange();
    } finally {
      setSavingId((cur) => (cur === id ? null : cur));
    }
  }

  return (
    <table className="admin-table">
      <thead>
        <tr>
          {COLUMN_LABELS.map((label) => (
            <th key={label}>{label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rsvps.map((r) => (
          <tr key={r.id}>
            <td data-label="Icon">
              {r.character_image_url && (
                <img
                  src={r.character_image_url}
                  alt={r.character_name ?? ''}
                  className="admin-character-avatar"
                />
              )}
            </td>
            <td data-label="Child">{r.child_name}</td>
            <td data-label="Parent" className="admin-parent-cell">
              <span>{r.parent_name}</span>
              <a href={`mailto:${r.parent_email}`}>{r.parent_email}</a>
              {r.parent_phone && <a href={`tel:${r.parent_phone}`}>{r.parent_phone}</a>}
            </td>
            <td data-label="Status">
              <select
                value={r.rsvp_status}
                onChange={(e) => setStatus(r.id, e.target.value as RsvpStatus)}
              >
                {RSVP_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABEL[s]}
                  </option>
                ))}
              </select>
            </td>
            <td data-label="Character">
              {r.character_name ?? '—'}
              {r.character_variant ? ` (${r.character_variant})` : ''}
            </td>
            <td data-label="Shirt size">{r.shirt_size ?? ''}</td>
            <td data-label="Admin notes">
              <input
                type="text"
                defaultValue={r.admin_notes ?? ''}
                onBlur={(e) => setNotes(r.id, e.target.value)}
              />
            </td>
            <td data-label="Actions">
              {r.character_id ? <button onClick={() => release(r.id)}>Release</button> : null}
              <button onClick={() => remove(r.id)}>Delete</button>
              {savingId === r.id && <span className="admin-saving">Saving…</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
